import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useEffect, useMemo, type ReactNode } from "react";
import { HttpClient, Session, localeOf } from "../api";
import { AuthProvider, useAuth } from "../auth";
import { setLocale } from "../i18n";
import { ThemeProvider, type ThemePreference } from "../theme";
import { type Brand } from "../theme/brand";
import { ConfirmProvider, Toaster } from "../ui";
import { ShellConfigProvider } from "./context";
import { JobCenterProvider } from "./jobs/JobCenterProvider";
import { ErrorBoundary } from "./ErrorBoundary";
import { LoginView } from "./LoginView";
import type { NavItem } from "./nav";

export interface AppRootProps {
  brand: Brand;
  nav: NavItem[];
  /** Same-origin path nginx proxies to this app's backend, e.g. `/api/2.0`. */
  basePath: string;
  /** All three apps pass "system"; kept as a prop so a deployment can pin one. */
  defaultTheme?: ThemePreference;
  /**
   * The authenticated app — normally `<RouterProvider router={router} />`.
   *
   * Note the shell is *not* wrapped around this. It renders inside the router,
   * as the root route's component (`ShellLayout`), so its navigation links have
   * a router context. See ShellLayout for why.
   */
  children: ReactNode;
}

/**
 * The composition root every app mounts.
 *
 * Owning providers, the boot gate and the shell config here is what keeps each
 * app down to a brand, a nav manifest and a route tree.
 */
export function AppRoot({ brand, nav, basePath, defaultTheme = "light", children }: AppRootProps) {
  const { session, http, queryClient } = useMemo(() => {
    const nextSession = new Session({ basePath });
    return {
      session: nextSession,
      http: new HttpClient(nextSession),
      queryClient: new QueryClient({
        defaultOptions: {
          queries: {
            // Grid data is read constantly and rarely changes underfoot; a
            // short stale window beats refetching on every focus change.
            staleTime: 30_000,
            retry: (failureCount, error) => {
              // 401 is handled by the refresh path, 403 will never succeed on
              // retry, and 404 is an answer. Only retry genuine flakiness.
              const status = (error as { status?: number })?.status;
              if (status === 401 || status === 403 || status === 404) return false;
              return failureCount < 2;
            },
          },
        },
      }),
    };
  }, [basePath]);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider defaultPreference={defaultTheme}>
          <AuthProvider session={session} http={http}>
            <ShellConfigProvider brand={brand} nav={nav}>
              <ConfirmProvider>
                <JobCenterProvider>
                  <Gate brand={brand}>{children}</Gate>
                  <Toaster position="bottom-center" closeButton richColors />
                </JobCenterProvider>
              </ConfirmProvider>
            </ShellConfigProvider>
          </AuthProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

function Gate({ brand, children }: { brand: Brand; children: ReactNode }) {
  const { status, account } = useAuth();

  // The account is the authority on language; local storage only carries the
  // choice between the app opening and the account arriving. Applying it here
  // is what makes the preference follow someone to a browser they have never
  // used, which is the whole reason it is stored server-side.
  const language = account?.settings?.language;
  useEffect(() => {
    if (!language) return;
    const wanted = localeOf(language);
    if (document.documentElement.lang !== wanted) void setLocale(wanted);
  }, [language]);

  if (status === "booting") {
    return (
      <div className="flex h-full items-center justify-center bg-ground">
        <span
          aria-label="Loading"
          className="size-6 animate-spin rounded-full border-2 border-accent border-t-transparent"
        />
      </div>
    );
  }

  if (status === "anonymous") {
    return <LoginView brand={brand} />;
  }

  // Authenticated: hand off to the router, which renders the shell via its
  // root route.
  return <>{children}</>;
}
